import React, { useState, useEffect, useRef } from 'react';
import { Eye, Mail, Phone, User, Search, GraduationCap, ChevronLeft, ChevronRight, ArrowLeft, Users, Award, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Select from '../../components/Common/Select';
import api, { getFileUrl } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import SummaryCard from '../../components/Common/SummaryCard';

const StudentManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overallTotalStudents, setOverallTotalStudents] = useState(0);
  const [collegeDegrees, setCollegeDegrees] = useState([]);
  const [batches, setBatches] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 12
  });

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedDegreeId, setSelectedDegreeId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [profileCompleteFilter, setProfileCompleteFilter] = useState('');

  const debounceTimer = useRef(null);



  // ---------------------------------------------------------------------
  // Data Fetching
  // ---------------------------------------------------------------------
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const collegeId = user.college?._id || user.college;
      if (collegeId) {
        const params = {
          role: 'student',
          college: collegeId,
          limit: pagination.limit,
          page: pagination.currentPage,
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(selectedBatch && { batch: selectedBatch }),
          ...(selectedDegreeId && { degree: selectedDegreeId }),
          ...(selectedDepartment && { department: selectedDepartment }),
          ...(statusFilter && { status: statusFilter }),
          ...(profileCompleteFilter && { profileComplete: profileCompleteFilter })
        };
        const { data } = await api.get('/users', { params });
        if (data.success) {
          setStudents(data.data || []);
          setPagination(prev => ({
            ...prev,
            total: data.total || 0,
            totalPages: data.totalPages || 0,
            currentPage: data.currentPage || 1
          }));
          if (!debouncedSearch && !selectedBatch && !selectedDegreeId && !selectedDepartment && !statusFilter && !profileCompleteFilter) {
            setOverallTotalStudents(data.total || 0);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const fetchCollegeDegrees = async () => {
    try {
      const { data } = await api.get('/college-degrees');
      if (data.success) setCollegeDegrees(data.data || []);
    } catch (error) {
      console.error('Error fetching degrees:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const { data } = await api.get('/users/batches');
      if (data.success) setBatches(data.data || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };



  // Debounce search input
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch, selectedBatch, selectedDegreeId, selectedDepartment, statusFilter, profileCompleteFilter]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedBatch, selectedDegreeId, selectedDepartment, statusFilter, profileCompleteFilter, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    fetchCollegeDegrees();
    fetchBatches();
  }, []);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------
  const handleView = (student) => {
    navigate(`/college/students/${student._id}`);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(newLimit),
      currentPage: 1
    }));
  };

  const getProfileCompletion = (student) => {
    if (typeof student.profileCompletion === 'number') {
      return student.profileCompletion;
    }
    const fields = [
      student.fullName,
      student.email,
      student.mobile,
      student.rollNumber,
      student.batch,
      student.degree,
      student.dateOfBirth,
      student.gender,
      student.idProof,
      student.parentName,
      student.parentMobile,
      student.address?.street || student.address?.city || student.address?.state || student.address?.pincode
    ];
    const filled = fields.filter(val => val !== undefined && val !== null && String(val).trim() !== '');
    return Math.round((filled.length / fields.length) * 100);
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="border border-gray-100 dark:border-[#1a3884]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#002147]">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[#1a3884]/30 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 text-gray-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-0.5">
            <h1 className="text-[22px] font-black text-[#1a3884] dark:text-[#4f7dff] tracking-tight">
              Student Overview
            </h1>
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <Users className="w-4 h-4 text-[#1a3884]/60 dark:text-[#4f7dff]/60" />
              Showing {students.length} of {pagination.total} students in your institution
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <SummaryCard
          title="Total Students"
          value={overallTotalStudents}
          icon={Users}
          loading={loading}
          layout="row"
        />
        <SummaryCard
          title="Total Degrees"
          value={new Set(collegeDegrees.map(d => d.fullName || d.abbreviation)).size}
          icon={GraduationCap}
          loading={loading}
          layout="row"
        />
        <SummaryCard
          title="Total Specializations"
          value={collegeDegrees.length}
          icon={Award}
          loading={loading}
          layout="row"
        />
        <SummaryCard
          title="Total Batches"
          value={batches.length}
          icon={Calendar}
          loading={loading}
          layout="row"
        />
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white dark:bg-[#002147] rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-[#1a3884]/30 space-y-4">
        {/* Search Input and Reset Button */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <div className="flex-grow w-full relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
            <input
              type="text"
              placeholder="Search students by name, email, roll number..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-[#1a3884]/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] dark:focus:ring-[#4f7dff] focus:border-transparent text-sm bg-gray-50 dark:bg-[#001f40] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex-shrink-0 w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto py-2.5 px-4 text-sm font-semibold flex items-center justify-center gap-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300 bg-white dark:bg-transparent"
              onClick={() => {
                setSearchTerm('');
                setDebouncedSearch('');
                setSelectedDepartment('');
                setSelectedBatch('');
                setSelectedDegreeId('');
                setStatusFilter('');
                setProfileCompleteFilter('');
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Cascading Filter Dropdowns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Select
              value={selectedDegreeId}
              onChange={(e) => setSelectedDegreeId(e.target.value)}
              options={collegeDegrees
                .sort((a, b) => a.fullName.localeCompare(b.fullName))
                .map(deg => ({
                  value: deg._id,
                  label: `${deg.fullName} (${Array.isArray(deg.specialization) ? deg.specialization.join(', ') : deg.specialization})`
                }))}
              placeholder="All Degrees"
              className="w-full"
            />
          </div>

          <div>
            <Select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              options={batches.map(b => ({
                value: b,
                label: b
              }))}
              placeholder="All Batches"
              className="w-full"
            />
          </div>

          <div>
            <Select
              value={profileCompleteFilter}
              onChange={(e) => setProfileCompleteFilter(e.target.value)}
              options={[
                { value: 'completed', label: 'Completed' },
                { value: 'not_completed', label: 'Incompleted' }
              ]}
              placeholder="Profile Completion"
              className="w-full"
            />
          </div>

          <div>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
              placeholder="All Status"
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      {loading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl border border-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : students.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Students Found</h3>
            <p className="text-gray-500">There are no students matching your filter criteria.</p>
          </div>
        </Card>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Student Details
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Contact Info
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Degree Details
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Profile Completion
                  </th>
                  <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map((student) => (
                  <tr
                    key={student._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-full flex items-center justify-center text-white font-bold shadow-sm overflow-hidden">
                          {student.profileImage ? (
                            <img src={getFileUrl(student.profileImage)} className="w-full h-full object-cover" alt="Student Profile" />
                          ) : (
                            student.fullName?.charAt(0)?.toUpperCase() || 'S'
                          )}
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-gray-900 leading-tight">
                            {student.fullName || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center">
                            <span className="text-gray-400 font-medium">Roll No:</span>{' '}
                            <span className="ml-1 text-gray-600 font-semibold">{student.rollNumber || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center text-xs text-gray-600">
                          <Mail className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-gray-400" />
                          <span className="truncate max-w-[180px]">{student.email || 'N/A'}</span>
                        </div>
                        <div className="flex items-center text-xs text-gray-600">
                          <Phone className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-gray-400" />
                          <span>{student.mobile || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-3.5">
                      {student.degree ? (
                        <div className="flex flex-col">
                          <div className="flex items-center text-xs font-semibold text-gray-800">
                            <GraduationCap className="w-4 h-4 mr-1.5 text-primary-500 flex-shrink-0" />
                            <span>
                              {student.degree.fullName} ({student.degree.abbreviation})
                            </span>
                          </div>
                          {student.degree.specialization && (
                            <div className="text-[11px] text-gray-500 mt-0.5 pl-5 italic">
                              Spec: {Array.isArray(student.degree.specialization) ? student.degree.specialization.join(', ') : student.degree.specialization}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No Degree Assigned</span>
                      )}
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      {(() => {
                        const completion = getProfileCompletion(student);
                        return (
                          <span className={`text-xs font-semibold ${completion >= 80
                            ? 'text-green-600 dark:text-green-400'
                            : completion >= 50
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                            }`}>
                            {completion}%
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-3.5 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${student.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}
                      >
                        {student.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          type="button"
                          onClick={() => handleView(student)}
                          className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-all"
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Results Per Page */}
        <div className="flex items-center space-x-3 text-sm text-gray-600">
          <span>Results per page:</span>
          <select
            value={pagination.limit}
            onChange={(e) => handleLimitChange(e.target.value)}
            className="border border-gray-300 rounded-lg bg-gray-50 px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
          >
            {[12, 24, 48, 96].map(limit => (
              <option key={limit} value={limit}>{limit}</option>
            ))}
          </select>
        </div>

        {/* Status Info */}
        <div className="text-sm text-gray-600">
          Showing <span className="font-bold text-gray-900">{pagination.total === 0 ? 0 : (pagination.currentPage - 1) * pagination.limit + 1}</span> to{' '}
          <span className="font-bold text-gray-900">
            {Math.min(pagination.currentPage * pagination.limit, pagination.total)}
          </span>{' '}
          of <span className="font-bold text-gray-900">{pagination.total}</span> students
        </div>

        {/* Navigation Buttons */}
        {pagination.totalPages > 1 && (
          <nav className="flex items-center space-x-1" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="First Page"
            >
              <div className="flex -space-x-1">
                <ChevronLeft className="w-5 h-5 text-gray-600" />
                <ChevronLeft className="w-5 h-5 -ml-3 text-gray-600" />
              </div>
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Previous Page"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>

            {/* Page Numbers */}
            <div className="hidden sm:flex items-center space-x-1">
              {[...Array(pagination.totalPages)].map((_, idx) => {
                const pageNum = idx + 1;
                if (
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${pagination.currentPage === pageNum
                        ? 'bg-primary-600 text-white shadow-lg shadow-primary-200 scale-105'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-primary-300'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                if (pageNum === pagination.currentPage - 2 || pageNum === pagination.currentPage + 2) {
                  return <span key={pageNum} className="px-1 text-gray-400">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Next Page"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              title="Last Page"
            >
              <div className="flex -space-x-1">
                <ChevronRight className="w-5 h-5 text-gray-600" />
                <ChevronRight className="w-5 h-5 -ml-3 text-gray-600" />
              </div>
            </button>
          </nav>
        )}
      </div>


    </div>
  );
};

export default StudentManagement;
